import React, { FC, useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardSubtitle, CardTitle, Col, ListGroup, ListGroupItem, Row } from "reactstrap";
import { formatCurrency } from "../../../utils/formats";
import invoicesService from "../../../services/invoices.service";
import { toast } from "react-toastify";
import { select2Styles } from "../../../helpers/functions_helper";
import { yearOptions } from "../../../helpers/functions_helper";
import { noData } from "../../../utils/data";
import LoadingSpinner from "../../../components/Common/Loader";
import Chart from "../../../components/Chart";
import { currentYear } from "../../../common/staticdata";

type SalesReportProps = {
    hideHeading?: boolean;
}
const SalesReport: FC<SalesReportProps> = ({ hideHeading = false }) => {

    const { t } = useTranslation();

    const [loadingText, setLoadingText] = useState<string>('Loading...');
    const [loading, setLoading] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<any>({
        sale_report_year: currentYear
    })
    const [categories, setCategories] = useState<any>([]);
    const [lineChart, setLineChart] = useState<any>({
        series: [{
            name: t('Total Sales'),
            data: []
        }, {
            name: t('Total Income'),
            data: []
        }],
        options: {
            chart: {
                toolbar: {
                    show: false
                }
            },
            legend: {
                show: false,
                floating: false,
                fontSize: '16px',
                position: 'left',
                offsetX: 160,
                offsetY: 15,
                labels: {
                    useSeriesColors: true,
                },
            },
            noData: noData,
            dataLabels: {
                enabled: false,
                enabledOnSeries: undefined,
                formatter: function (value: any, opts: any) {
                    return formatCurrency(value);
                },
            },
            yaxis: {
                type: 'string',
                labels: {
                    formatter: function (value: any) {
                        return formatCurrency(value);
                    }
                },
            },
            stroke: {
                curve: 'smooth'
            },
            xaxis: {
                type: 'string',
                categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
            },
            tooltip: {
                x: {
                    format: 'dd/MM/yy HH:mm'
                },
            },
        },
    }
    )
    const getSalesReport = async (year: any) => {
        try {
            const res = await invoicesService.getSalesReport(year);
            if (res.status === 'success') {
                setCategories(res?.total_sale?.map((i: any) => i.month_name));
                res?.total_sale?.length > 0 ?
                    setLineChart((state: any) => ({
                        ...state,
                        series: [{
                            name: t('Total Sales'),
                            data: res?.total_sale?.map((i: any) => formatCurrency(i.total))
                        }, {
                            name: t('Total Income'),
                            data: res?.total_sale?.map((i: any) => formatCurrency(i.paid_amount))
                        }],
                        options: {
                            ...state.options,
                            chart: {
                                toolbar: {
                                    show: false
                                }
                            },
                            xaxis: {
                                type: 'string',
                                categories: res?.total_sale?.map((i: any) => t(i.month_name))
                            },
                        }
                    })) : setLineChart((state: any) => ({
                        ...state,
                        series: [],
                        options: {
                            noData: noData
                        }
                    }))
            }
            setLoading(false);
        } catch (error: any) {
            toast(error)
        }
    }

    useEffect(() => {
        loading && getSalesReport(formValues.sale_report_year);
    }, [loading])

    useEffect(() => {
        setLineChart((state: any) => ({
            ...state,
            series: [{
                name: t('Total Sales'),
                data: state?.series[0]?.data
            }, {
                name: t('Total Income'),
                data: state?.series[1]?.data
            }],
            options: {
                ...state.options,
                chart: {
                    toolbar: {
                        show: false
                    }
                },
                xaxis: {
                    type: 'string',
                    categories: categories?.map((i: any) => t(i))
                },
            }
        }))
    }, [t])

    useEffect(() => {
        setLoading(true);
    }, [formValues.sale_report_year])

    return (
        <>
            <div className="d-sm-flex flex-wrap">
                <CardTitle className="mb-3">{!hideHeading && t("Sales report")}</CardTitle>
                <div className="ms-auto">
                    <Select
                        id="sale_report_year"
                        name="sale_report_year"
                        className="form-select-sm myClassName"
                        styles={select2Styles}
                        value={yearOptions.find((i: any) => i.value === formValues.sale_report_year)}
                        options={yearOptions}
                        onChange={(option: any) => {
                            setFormValues((state: any) => ({ ...state, sale_report_year: option.value }))
                        }}
                    />
                </div>
            </div>
            <Chart loading={loading} options={lineChart.options} series={lineChart.series} type="area" height={320} />
        </>

    )
}

export default SalesReport;